import React, { useEffect, useRef, useState, useCallback } from "react";
// Assuming EpRight is an SVG or Icon component
// import { EpRight } from "../../../components/college-details/components/NavigationTabs";
import TabContent from "./TabContent";
import EducationSidebarAdds from "./EducationSidebarAdds";
import data from "./data/educationSidebarData.json";

// Placeholder for EpRight if not imported correctly
const EpRight = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 16 16"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

interface Tab {
  tabName: string;
  tabTitle: string;
}

interface NavigationTabsProps {
  tabs: Tab[];
  boardName: string;
}

const NavigationTabs: React.FC<NavigationTabsProps> = ({ tabs, boardName }) => {
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.tabName || "");
  const [isSticky, setIsSticky] = useState(false);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const leftContentRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState<number>(0);
  const [visibleSections, setVisibleSections] = useState<any[]>([]);
  const [isContentAvailable, setIsContentAvailable] = useState<boolean>(true);

  const calculateLeftHeight = useCallback((hasContent: boolean) => {
    setIsContentAvailable(hasContent);
    // Use requestAnimationFrame to wait for potential layout shifts
    requestAnimationFrame(() => {
      if (leftContentRef.current) {
        setLeftHeight(leftContentRef.current.clientHeight);
      }
    });
  }, []);

  useEffect(() => {
    // Original scroll listener removed to prevent sticky behavior
    // If you need other scroll-related functionality, keep it here
    // but remove the setIsSticky line
  }, []); // Re-run if leftContentRef changes (although unlikely)

  // Memoize checkScroll function to ensure stable identity for useEffect dependency
  const checkScroll = useCallback(() => {
    const el = tabContainerRef.current;
    if (el) {
      const currentScroll = Math.round(el.scrollLeft); // Round for consistency
      const maxScroll = Math.round(el.scrollWidth - el.clientWidth);

      setCanScrollLeft(currentScroll > 0);
      // Use a small tolerance (e.g., 1px) for floating point inaccuracies
      setCanScrollRight(currentScroll < maxScroll - 1);
      // console.log(`Scroll Check: Left=${currentScroll > 0}, Right=${currentScroll < maxScroll - 1}, scrollLeft=${currentScroll}, maxScroll=${maxScroll}`);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  }, []); // No dependencies needed, uses ref and setters

  // Effect to handle scroll/resize events for updating arrow states
  useEffect(() => {
    const el = tabContainerRef.current;
    if (!el) return; // Exit if ref not available yet

    // Initial check
    checkScroll();

    // Add listeners
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    // Create a ResizeObserver to detect content changes affecting scrollWidth
    const resizeObserver = new ResizeObserver(checkScroll);
    // Observe the immediate children (ul) or the container itself
    // Observing children might be more reliable if the container size is fixed but content changes
    const listElement = el.querySelector("ul");
    if (listElement) {
      resizeObserver.observe(listElement);
    } else {
      resizeObserver.observe(el); // Fallback to container
    }

    // Cleanup function
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      resizeObserver.disconnect(); // Disconnect observer
    };
  }, [checkScroll]); // Re-run effect if checkScroll function identity changes (it shouldn't due to useCallback)

  const scrollTabs = (dir: "left" | "right") => {
    const el = tabContainerRef.current;
    if (el) {
      // Scroll by a fraction of the visible width for smoother steps
      const scrollAmount = el.clientWidth * 0.7; // Adjust multiplier as needed
      el.scrollBy({
        left: dir === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
      // NOTE: We DON'T need to call checkScroll() here manually.
      // The 'scroll' event listener added in the useEffect hook
      // will be triggered automatically by scrollBy, and it will call checkScroll.
    }
  };

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
    window.history.replaceState(null, "", `#${tabName}`);
    // Maybe scroll the selected tab into view if it's off-screen
    const tabElement = tabContainerRef.current?.querySelector(
      `li[data-tabname="${tabName}"]`
    );
    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  };

  useEffect(() => {
    let totalHeight = 0;
    const selectedSections: any[] = [];
    const maxHeight = leftHeight || 400; // Use calculated height or a fallback

    data.forEach((section: any) => {
      // Estimate height: Adjust these numbers based on your actual sidebar item styling
      const sectionHeaderHeight = 30;
      const itemHeight = 40;
      const sectionHeight =
        sectionHeaderHeight + section.items.length * itemHeight;

      if (totalHeight + sectionHeight <= maxHeight) {
        selectedSections.push(section);
        totalHeight += sectionHeight;
      } else {
        // Optional: If you want to partially show a section if it fits
        // you might add more complex logic here.
      }
    });

    // Ensure at least some content is shown if leftHeight is very small initially
    setVisibleSections(
      selectedSections.length > 0 ? selectedSections : data.slice(0, 1) // Show at least one section
    );
  }, [leftHeight, activeTab]); // Re-calculate when leftHeight or activeTab changes

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const foundTab = tabs.find((tab) => tab.tabName === hash);
    setActiveTab(foundTab ? foundTab.tabName : tabs[0]?.tabName || "");
    // Check scroll again after potential tab change / initial load
    requestAnimationFrame(checkScroll);
  }, [tabs, checkScroll]); // Add checkScroll as dependency

  return (
    <div className="relative">
      {/* Tab Navigation Bar */}
      <div
        className="relative bg-gradient-to-r from-[#173CBA] to-[#06AFA9]"
        style={{
          background: "linear-gradient(180deg, #173CBA 0%, #06AFA9 100%)",
        }}
      >
        <div className="container mx-auto flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2">
          {/* Left Arrow */}
          <button
            aria-label="Scroll tabs left"
            disabled={!canScrollLeft}
            className={`bg-[#00CC99] border border-white rounded text-white p-2 shrink-0 hidden sm:block transition-opacity duration-200 ${
              !canScrollLeft
                ? "opacity-50 cursor-not-allowed"
                : "opacity-100 hover:bg-[#00B489]"
            }`}
            onClick={() => scrollTabs("left")}
          >
            <EpRight className="rotate-180" />
          </button>

          {/* Tab Container */}
          <div
            ref={tabContainerRef}
            // --- Tailwind Scrollbar Styling (add `scrollbar-thin` etc. if using plugin) ---
            // Example using standard CSS scrollbar hiding (might need browser prefixes):
            className="flex-grow overflow-x-auto whitespace-nowrap scroll-smooth scrollbar-hide"
            // If using tailwind-scrollbar plugin:
            // className="flex-grow overflow-x-auto whitespace-nowrap scroll-smooth scrollbar-thin scrollbar-thumb-[#00B489] scrollbar-track-transparent py-1"
          >
            <ul className="list-none flex gap-3 sm:gap-4 items-center text-center w-max px-1">
              {" "}
              {/* Use w-max to allow shrinking */}
              {tabs.map((tab) => (
                <li
                  key={tab.tabName}
                  data-tabname={tab.tabName} // Add data attribute for potential selection
                  className={`text-sm sm:text-base py-1.5 px-3 sm:px-3.5 font-semibold rounded-sm cursor-pointer transition-colors duration-200 ${
                    activeTab === tab.tabName
                      ? "bg-[#00B489] text-white" // Use a distinct active color
                      : "bg-white text-black hover:bg-gray-200"
                  }`}
                  onClick={() => handleTabChange(tab.tabName)}
                  role="tab" // Add role for accessibility
                  aria-selected={activeTab === tab.tabName}
                >
                  {tab.tabTitle}
                </li>
              ))}
            </ul>
          </div>

          {/* Right Arrow */}
          <button
            aria-label="Scroll tabs right"
            disabled={!canScrollRight}
            className={`bg-[#00CC99] border border-white rounded text-white p-2 shrink-0 hidden sm:block transition-opacity duration-200 ${
              !canScrollRight
                ? "opacity-50 cursor-not-allowed"
                : "opacity-100 hover:bg-[#00B489]"
            }`}
            onClick={() => scrollTabs("right")}
          >
            <EpRight />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row gap-6 lg:gap-9 py-6 md:py-8 px-2 sm:px-0">
          {/* Left Content */}
          <div
            ref={leftContentRef}
            className="w-full md:w-8/12 lg:w-9/12 min-h-[400px] md:min-h-[500px]"
          >
            <TabContent
              boardName={boardName}
              tabName={activeTab}
              onLoad={calculateLeftHeight}
            />
          </div>

          {/* Right Sidebar */}
          <div className="w-full md:w-4/12 lg:w-3/12 hidden md:block">
            {/* Render sidebar only if content available and sections calculated */}
            {isContentAvailable && visibleSections.length > 0 && (
              <div className="sticky top-20">
                {" "}
                {/* Make sidebar sticky relative to viewport */}
                <EducationSidebarAdds sections={visibleSections} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationTabs;
